import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { Folder, Clock, Search, Plus, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';

export default function CaseFeed() {
  const [search, setSearch] = useState('');
  const [showNewCase, setShowNewCase] = useState(false);
  const queryClient = useQueryClient();

  const { data: cases, isLoading } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  const filtered = (cases || []).filter(c =>
    !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.caseNumber.toLowerCase().includes(search.toLowerCase())
  );

  const handleCaseCreated = () => {
    setShowNewCase(false);
    queryClient.invalidateQueries({ queryKey: ['cases'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Case Feed</h1>
        <button
          onClick={() => setShowNewCase(true)}
          className="flex items-center gap-2 px-4 py-2 bg-rtcc-accent text-white text-sm font-medium rounded-lg hover:bg-rtcc-accent/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Case
        </button>
      </div>

      {showNewCase && <NewCaseModal onClose={() => setShowNewCase(false)} onCreated={handleCaseCreated} />}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-rtcc-panel border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rtcc-accent focus:border-transparent text-sm"
          placeholder="Search cases..."
        />
      </div>

      {/* List */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-500">Loading cases...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Folder className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p>No cases found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => (
            <Link
              key={c.caseNumber}
              to={`/cases/${c.caseNumber}`}
              className="block p-4 bg-rtcc-panel rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-rtcc-accent">{c.caseNumber}</span>
                    <StatusBadge status={c.status} />
                  </div>
                  <h3 className="text-white font-medium truncate">{c.title}</h3>
                  {c.description && (
                    <p className="text-sm text-slate-400 mt-1 line-clamp-2">{c.description}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 text-xs text-slate-500 flex-shrink-0">
                  <span>{c.evidenceCount || 0} evidence items</span>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  const colors = s === 'open' || s === 'active'
    ? 'bg-rtcc-accent/20 text-rtcc-accent'
    : s === 'pending' || s === 'pending review'
    ? 'bg-rtcc-warning/20 text-rtcc-warning'
    : 'bg-slate-700 text-slate-400';

  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${colors}`}>
      {status}
    </span>
  );
}

function NewCaseModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'Criminal' | 'Civil'>('Criminal');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: () => apiClient.createCase({ title, type, description: description || undefined }),
    onSuccess: onCreated,
    onError: (err: Error) => setError(err.message || 'Failed to create case'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setError('');
    mutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
      <div className="bg-rtcc-panel border border-slate-700 rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">New Case</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">
              Case Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 bg-rtcc-dark border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rtcc-accent text-sm"
              placeholder="e.g., Armed Robbery Investigation"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">
              Case Type *
            </label>
            <div className="flex gap-3">
              {(['Criminal', 'Civil'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                    type === t
                      ? 'bg-rtcc-accent/20 border-rtcc-accent text-rtcc-accent'
                      : 'bg-rtcc-dark border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 bg-rtcc-dark border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rtcc-accent text-sm resize-none"
              placeholder="Brief case description..."
              rows={3}
            />
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 py-2 px-4 bg-rtcc-accent text-white text-sm font-medium rounded-lg hover:bg-rtcc-accent/90 disabled:opacity-50 transition-colors"
            >
              {mutation.isPending ? 'Creating...' : 'Create Case'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
